import {
  BiDashboardOpKind,
  BiDashboardPermission,
  BiDashboardSummary,
  BiDashboardView,
} from '@euler/model/bi/dashboard';
import { useServiceFactory } from '@euler/services/factory';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { biByDefaultThenRecent } from '../../functions/biByDefaultThenRecent';
import { biIsStaffDashboard } from '../../functions/biIsStaffDashboard';

export interface BiDashboardListState {
  /** Null until the first read. */
  items: BiDashboardSummary[] | null;
  failed: boolean;
}

/** The store's dashboards the caller can see, and what the list page does. */
export function useBiDashboardList() {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiDashboardListState>({
    items: null,
    failed: false,
  });
  const [reload, setReload] = useState(0);

  // Read again on every return: a dashboard renamed, shared or emptied on
  // its own page changes this list.
  const load = useCallback(() => {
    let active = true;
    setState(current => ({ ...current, failed: false }));
    biService
      .dashboards()
      .then(items => {
        if (active) setState({ items, failed: false });
      })
      .catch(error => {
        console.error('BI dashboards failed', error);
        if (active) setState(current => ({ ...current, failed: true }));
      });
    return () => {
      active = false;
    };
  }, [biService]);
  useFocusEffect(load);
  useEffect(() => {
    if (reload) return load();
  }, [load, reload]);
  const refresh = useCallback(() => setReload(value => value + 1), []);

  const sections = useMemo(() => {
    const items = state.items ?? [];
    const owned = items.filter(
      item => item.permission === BiDashboardPermission.Manage,
    );
    return {
      mine: owned
        .filter(item => !biIsStaffDashboard(item))
        .sort(biByDefaultThenRecent),
      // A manager's dashboards about colleagues, by name.
      staff: owned
        .filter(biIsStaffDashboard)
        .sort((a, b) =>
          (a.subjectName ?? a.name).localeCompare(
            b.subjectName ?? b.name,
            'zh',
          ),
        ),
      shared: items
        .filter(item => item.permission !== BiDashboardPermission.Manage)
        .sort(biByDefaultThenRecent),
    };
  }, [state.items]);

  const create = useCallback(
    async (name: string): Promise<BiDashboardView> =>
      await biService.createDashboard({ name }),
    [biService],
  );
  const rename = useCallback(
    async (item: BiDashboardSummary, name: string) => {
      await biService.applyDashboardOps(item.id, {
        baseRevision: item.revision,
        ops: [{ op: BiDashboardOpKind.RenameDashboard, name }],
      });
      refresh();
    },
    [biService, refresh],
  );
  const remove = useCallback(
    async (item: BiDashboardSummary) => {
      await biService.deleteDashboard(item.id);
      refresh();
    },
    [biService, refresh],
  );

  return { state, sections, refresh, create, rename, remove };
}
